import { type IRestResponse } from "@app-rest/index";
import { fromIntegerToDecimal } from "@app-utilities/price";
import { useCloudBackend } from "@qamf/shell-app-sdk";

import type { IAttraction, IAttractionBookingFeeResponse, IAttractionBusinessDayResponse, IAttractionCartRequest, IAttractionCartResponse, IAttractionConfirmationEmail, IAttractionDepositsConfigurationResponse, IAttractionOfferDetails, IAttractionOffersRequest, IAttractionOffersResponse, IAttractionReservationChecksRequest, IAttractionReservationChecksResponse, IAttractionTimeSlotRequest, IAttractionTimeSlotResponse, ICheckSummary, IConfirmAttractionReservationRequest } from "./attractions.interfaces";

export default function useAttractionsRestClient() {
	const { get, post } = useCloudBackend();

	function getAttractionsList(systemId: number) {
		return get<IRestResponse<IAttraction>>(`/${systemId}/attractions-api/attractions`, { cacheInMemory: false });
	}

	async function attractionOffers(systemId: number, data: IAttractionOffersRequest) {
		const result = await post<IAttractionOffersResponse>(
			`${systemId}/attractions-api/itemsAvailability/search`, {
				body: data,
				cacheInMemory: false
			});
		if (result.data) {
			result.data.AvailableItems = result.data.AvailableItems.map(item => ({
				...item,
				Price: fromIntegerToDecimal(item.Price)
			}));
		}
		return result;
	}

	async function attractionItemDetails(systemId: number, itemId: string) {
		const result = await get<IAttractionOfferDetails>(`/${systemId}/attractions-api/attractions/catalog/${itemId}`, {
			cacheInMemory: false,
			throwExcluding: [{ statusCode: 404 }]
		});
		if (result.data)
			result.data.Price = fromIntegerToDecimal(result.data.Price);

		return result;
	}

	function attractionTimeSlots(systemId: number, data: IAttractionTimeSlotRequest, abortController?: AbortController) {
		return post<IAttractionTimeSlotResponse>(
			`${systemId}/attractions-api/itemsAvailability/slots/search`, {
				body: data,
				abortController,
				cacheInMemory: false
			});
	}

	function confirmAttractionReservation(systemId: number, data: IConfirmAttractionReservationRequest) {
		return post(
			`/${systemId}/attractions-api/carts/confirm`, {
				body: data,
				cacheInMemory: false,
				throwExcluding: [{ statusCode: 404 }]
			});
	}

	function getAttractionConfirmationEmail(systemId: number) {
		return get<IAttractionConfirmationEmail>(`/${systemId}/attractions-api/email-setup/confirmation/custom-texts`, { cacheInMemory: false });
	}

	async function attractionCartSummary(systemId: number, data: IAttractionCartRequest) {
		const result = await post<IAttractionCartResponse>(
			`/${systemId}/attractions-api/carts`, {
				body: data,
				cacheInMemory: false,
				throwExcluding: [{ statusCode: 404 }]
			});
		if (result.data) {
			for (const cartItem of result.data.CartItems) {
				for (const resource of cartItem.Data.MultiAttractionReservation.ReservedResources) {
					for (const item of resource.Items)
						item.MultiAttractionItem.Price = fromIntegerToDecimal(item.MultiAttractionItem.Price);

				}
				const bookingFee = cartItem.Data.MultiAttractionReservation.BookingFee;
				if (bookingFee)
					bookingFee.UnitPrice = fromIntegerToDecimal(bookingFee.UnitPrice);

				const summary = cartItem.Data.Summary as unknown as Record<string, number | undefined>;
				if (typeof summary.TotalMoneyAmount === "number")
					summary.TotalMoneyAmount = fromIntegerToDecimal(summary.TotalMoneyAmount);
				if (typeof summary.TaxMoneyAmount === "number")
					summary.TaxMoneyAmount = fromIntegerToDecimal(summary.TaxMoneyAmount);
			}
		}
		return result;
	}

	function attractionDepositConfiguration(systemId: number) {
		return get<IAttractionDepositsConfigurationResponse>(`/${systemId}/attractions-api/deposits/configurations`, { cacheInMemory: false });
	}

	async function attractionReservationChecks(systemId: number, data: IAttractionReservationChecksRequest) {
		const result = await post<IAttractionReservationChecksResponse>(`/${systemId}/attractions-api/checks/search`, {
			body: data,
			cacheInMemory: false
		});
		if (result.data) {
			for (const check of result.data.Checks) {
				for (const item of check.Items)
					item.UnitPrice = fromIntegerToDecimal(item.UnitPrice);

				for (const deposit of check.Deposits)
					deposit.InitialBalance = fromIntegerToDecimal(deposit.InitialBalance);

			}
		}
		return result;
	}

	async function attractionReservationCheckSummary(systemId: number, checkId: number) {
		const result = await get<ICheckSummary>(`/${systemId}/attractions-api/checks/${checkId}/summary`, {
			cacheInMemory: false,
			throwExcluding: [{ statusCode: 404 }]
		});
		if (result.data) {
			result.data.Summary.TotalMoneyAmount = fromIntegerToDecimal(result.data.Summary.TotalMoneyAmount);
			result.data.Summary.TaxMoneyAmount = fromIntegerToDecimal(result.data.Summary.TaxMoneyAmount);
		}
		return result;
	}

	async function attractionBookingFee(systemId: number) {
		const result = await get<IAttractionBookingFeeResponse>(`/${systemId}/attractions-api/bookingfee`, {
			cacheInMemory: false,
			throwExcluding: [
				{ statusCode: 404 } // 404 means no booking fee configured on QPortal first time
			]
		});
		if (result.data?.BookingFee) {
			const fee = result.data.BookingFee;
			if (fee.Fixed) fee.Fixed.Amount = fromIntegerToDecimal(fee.Fixed.Amount);
			if (fee.Percentage) fee.Percentage.CapAmount = fromIntegerToDecimal(fee.Percentage.CapAmount);
		}
		return result;
	}

	function getAttractionBusinessDay(systemId: number) {
		return get<IAttractionBusinessDayResponse>(`/${systemId}/attractions-api/reservation-parameters/business-day`);
	}

	return {
		getAttractionsList,
		attractionOffers,
		attractionItemDetails,
		attractionTimeSlots,
		confirmAttractionReservation,
		getAttractionConfirmationEmail,
		attractionCartSummary,
		attractionDepositConfiguration,
		attractionReservationChecks,
		attractionReservationCheckSummary,
		attractionBookingFee,
		getAttractionBusinessDay
	};
}
